import axios from 'axios';
import { log } from '@/Utils/logger';
import { v4 as uuidv4 } from 'uuid';

import { useSwalDialog } from '@/use/swalDialog';
import { tokenExpiredText } from '@/constants/app';
import { requestFrontendPopupInfo } from '@/use/frontendPopupInfoContext';
import { FrontendPopupFlowName } from '@/gql/graphql';

const { showErrorPopupDialog } = useSwalDialog();

const baseUrl = import.meta.env.VITE_API_URL;

const config: any = {
  address: baseUrl,
  port: '',
  basePath: '',
  axiosConfig: {
    baseURL: baseUrl,
    headers: {},
    withCredentials: false,
    crossDomain: true,
  },
  getEndpointUrl() {
    return this.address + (this.basePath ? this.basePath : '');
  },
};

if (localStorage.getItem('access_token') || sessionStorage.getItem('access_token')) {
  config.axiosConfig.headers['authorization'] =
    'Bearer ' + (localStorage.getItem('access_token') || sessionStorage.getItem('access_token'));
}

const $http = axios.create(config.axiosConfig);
const interceptors = $http.interceptors;

if (localStorage.getItem('access_token') || sessionStorage.getItem('access_token')) {
  $http.defaults.headers.common['authorization'] =
    'Bearer ' + (localStorage.getItem('access_token') || sessionStorage.getItem('access_token'));
}

// Endpoints using extend-timeout return a 200 status to keep long requests alive,
// Errors that happen after that are returned in the body
// if we receive one of them then throw
const throwIfErrorBodyInSuccessResponse = (result: any) => {
  const bodyStatus = Number(result?.data?.status);

  if (!Number.isInteger(bodyStatus) || bodyStatus < 400) {
    return;
  }

  throw Object.assign(new Error(result.data.message ?? `Request failed with status code ${bodyStatus}`), {
    response: { ...result, status: bodyStatus },
  });
};

const runAndLogAxios = async <T>(method: string, url: string, operation: () => Promise<T>, payload?: any): Promise<T> => {
  const requestId = uuidv4();

  log.info(
    {
      func: 'runAndLogAxios',
      options: {
        method,
        url,
        payload: url === 'login' ? { email: payload.email } : payload,
      },
      requestId,
    },
    `Axios init of HTTP ${method}`,
  );

  try {
    const result = await operation();
    throwIfErrorBodyInSuccessResponse(result);

    log.info(
      {
        func: 'runAndLogAxios',
        options: {
          method,
          url,
        },
        requestId,
      },
      `Axios success of HTTP ${method}`,
    );

    return result;
  } catch (error: any) {
    const errorCode = error.response?.data?.code;
    const errorMessage = error.response?.data?.message ?? '';

    if (errorCode === 401 && errorMessage.includes(tokenExpiredText)) {
      showErrorPopupDialog({
        text: 'Your session has expired. Please log out and log back in to continue using the dashboard.',
      });
    }

    log.error(
      {
        func: 'runAndLogAxios',
        options: {
          method,
          url,
        },
        requestId,
        error,
      },
      `Axios error of HTTP ${method}`,
    );

    throw error;
  }
};

const sendPost = async (url: string, payload?: any, configParam?: any) => {
  let urlParsed;
  try {
    urlParsed = new URL(url);
  } catch {
    console.log('.');
  }

  if (urlParsed?.pathname === '/callRideFromEstimate') {
    const phoneNumber = urlParsed.searchParams.get('phoneNumber');
    if (phoneNumber)
      requestFrontendPopupInfo({
        callerPhoneNumber: phoneNumber,
        flowName: FrontendPopupFlowName.GourmetCrossSale,
      });
  }

  const result = await runAndLogAxios('POST', url, () => $http.post(url, payload, configParam), payload);

  return result;
};

const sendPatch = async (url: string, payload: any = null, headers: any = null) => {
  return runAndLogAxios('PATCH', url, () => $http.patch(url, payload, headers), payload);
};

const sendPut = async (url: string, payload: any, headers: any = null) => {
  return runAndLogAxios('PUT', url, () => $http.put(url, payload, headers), payload);
};

const sendGet = async (url: string, params: any = null) => {
  return runAndLogAxios('GET', url, () => $http.get(url, { params }));
};

const sendDelete = async (url: string, payload: any = null) => {
  return runAndLogAxios('DELETE', url, () => $http.delete(url, payload), payload);
};

export default config;
export { config };
export { sendPost };
export { sendGet };
export { sendPatch };
export { sendPut };
export { sendDelete };
export { interceptors };
